import { find, get, sortBy, findIndex } from 'lodash';
import { connect } from 'react-redux';

import MasterProductCard from 'components/CustomCollectionProductCard/MasterProductCard';
import { COLLECTION_ITEM_TYPES } from 'constants/collection';
import * as actions from 'state/actions';
import { ADD_TO_CART_ANALYTICS_ORIGINS } from 'state/constants';
import Catalog from 'state/selectors/catalog';
import { getMasterProductData, getMasterProductCarouselIndex } from 'state/selectors/collection';
import CollectionPage from 'state/selectors/collection-page';
import Filters from 'utils/Filters';
import Rudderstack from 'utils/Rudderstack';
import Settings from 'utils/Settings';

const DEFAULT_N_COLORS_PER_SLIDE = 6;

const mapStateToProps = (
  state,
  {
    masterHandle,
    initialColorwayHandle,
    nItemsPerSlide = DEFAULT_N_COLORS_PER_SLIDE,
    appearance,
    hasMobileQuickAddRow,
    ...otherProps
  }
) => {
  let renamedMasterHandle = masterHandle;
  if (otherProps.productType === COLLECTION_ITEM_TYPES.COLORWAY) {
    const sufix = initialColorwayHandle || COLLECTION_ITEM_TYPES.COLORWAY;
    renamedMasterHandle = `${masterHandle}-${sufix}`;
  }
  const masterProduct = Catalog.getMasterProduct(state, masterHandle);
  const isMasterProductLoading = Catalog.isMasterProductLoading(state, masterHandle);
  const { enableDiscountPriceColor } = Settings;

  if (!masterProduct && !isMasterProductLoading) {
    throw new Error(`Could not find master product with handle "${masterHandle}"`);
  }

  const availableColorways = Catalog.getAvailableMasterProductColorways(state, masterHandle);
  const masterProductCacheKey = Catalog.getMasterProductCacheKey(state, masterHandle);
  const cacheKey = CollectionPage.getCollectionFilterCacheKey(state, masterProductCacheKey);

  const filteredColorways = Filters.executeFilters({
    items: availableColorways,
    filterItems: CollectionPage.getFilteringEngineFilterItems(state),
    cacheKey,
  });

  // Move initialColorwayHandle to the front, if it exists
  let filteredSortedColorways = filteredColorways;
  if (initialColorwayHandle) {
    filteredSortedColorways = sortBy(filteredColorways, ({ handle }) =>
      handle === initialColorwayHandle ? 0 : 1
    );
  }
  const masterProductData = getMasterProductData(state, renamedMasterHandle);

  const currentColorwayHandle = get(masterProductData, ['currentProduct']);

  // See if the selected product is in the filtered list. If no, fallback on first product in master product collection
  let currentColorway = find(filteredSortedColorways, { handle: currentColorwayHandle });

  if (!currentColorway) {
    currentColorway = get(filteredSortedColorways, [0]);
  }

  const colorwayPickerSlides = CollectionPage.buildColorwayPickerSlides({
    colorways: filteredSortedColorways,
    nItemsPerSlide,
    cacheKey: `${cacheKey}--${nItemsPerSlide}`,
  });

  const currentSlideIndex = getMasterProductCarouselIndex(state, renamedMasterHandle);

  // If price tiers are present, pass them through as query params to the currentColorwayUrl
  const handle = CollectionPage.getCollectionHandle(state);
  const parentCollection = Catalog.getParentCollection(state, handle);
  const priceTiers = get(parentCollection, 'priceTiers');
  let currentColorwayUrl = currentColorway.url;
  if (priceTiers && priceTiers.length > 0) {
    const url = new URL(currentColorwayUrl, window.location);
    url.searchParams.set('price-tiers', priceTiers);
    currentColorwayUrl = `${url.pathname}?${url.searchParams.toString()}`;
  }

  const trackNavigation = ({ handle, url, event }) => {
    event?.preventDefault();
    event?.stopPropagation();

    const collectionHandle = CollectionPage.getCollectionHandle(state);
    const parentCollection = Catalog.getParentCollection(state, collectionHandle);
    const collectionItems = CollectionPage.getCollectionItems(
      state,
      parentCollection.masterProducts
    );
    const collectionInfo = CollectionPage.getCollectionMetadata(
      state,
      collectionHandle,
      parentCollection
    );
    const clickedColorway = Catalog.getColorway(state, handle);

    const position = findIndex(collectionItems, { master: clickedColorway.master });

    Rudderstack.navigationDelayForIphoneAndSafari({
      analyticsFn: () => {
        Rudderstack.productClicked({
          collection: collectionInfo,
          collectionItem: clickedColorway,
          position,
        });
      },
      urlToNavigate: url,
    });
  };

  return {
    appearance,
    currentColorway: {
      url: currentColorwayUrl,
      handle: currentColorway.handle,
      masterName: currentColorway.masterName,
      colorName: currentColorway.colorName,
      price: currentColorway.formattedPrice,
      compareAtPrice: currentColorway.formattedCompareAtPrice,
      image: currentColorway.collectionProductCardImage,
      colors: currentColorway.colors,
      sortedSizes: currentColorway.sortedSizes,
      type: currentColorway.type,
      swatchImageUrl: currentColorway.swatchImageUrl,
      badge: currentColorway.badge || '',
    },
    masterHandle: renamedMasterHandle,
    initialColorwayHandle,
    colorwayPickerSlides,
    hasMobileQuickAddRow,
    currentSlideIndex,
    enableDiscountPriceColor,
    nItemsPerSlide,
    trackNavigation,
  };
};

const mapDispatchToProps = dispatch => ({
  setMasterProductCardProduct: ({ masterHandle, productHandle }) => {
    dispatch(actions.setMasterProductCardProduct({ masterHandle, productHandle }));
  },
  onSizeSelection: (variantId, targetSelector) => {
    dispatch(
      actions.addLineItems(
        { [variantId]: 1 },
        { targetSelector, origination: ADD_TO_CART_ANALYTICS_ORIGINS.QUICK_ADD }
      )
    );
  },
  setMasterProductCardCarouselIndex: ({ masterHandle, carouselIndex }) => {
    dispatch(actions.setMasterProductCardCarouselIndex({ masterHandle, carouselIndex }));
  },
});

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  onSwatchSelect: productHandle =>
    dispatchProps.setMasterProductCardProduct({
      masterHandle: stateProps.masterHandle,
      productHandle,
    }),
  onSlideIndexIncrement: () =>
    dispatchProps.setMasterProductCardCarouselIndex({
      masterHandle: stateProps.masterHandle,
      carouselIndex: stateProps.currentSlideIndex + 1,
    }),
  onSlideIndexDecrement: () =>
    dispatchProps.setMasterProductCardCarouselIndex({
      masterHandle: stateProps.masterHandle,
      carouselIndex: stateProps.currentSlideIndex - 1,
    }),
});

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(MasterProductCard);
